import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "../apiConfig";
import "./activerole.css";
import "./viewProject.css";
import { Funnel, Search, ChevronLeft, Trash2 } from "lucide-react";

const ViewProjects = () => {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [projects, setProjects] = useState([]);
  const [clientImage, setClientImage] = useState("");
  const [clientName, setClientName] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    fetchProjects();
  }, [roleId]);

  const fetchProjects = async () => {
    const token = localStorage.getItem("accessToken");
    setLoading(true);

    try {
      if (!token) {
        throw new Error("Unauthorised access");
      }

      const projectResponse = await fetch(
        `${BASE_URL}/project/get_all_projects`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ jobrole_id: roleId }),
        }
      );

      if (!projectResponse.ok) {
        throw new Error("Failed to fetch");
      }

      const data = await projectResponse.json();

      if (data && data.length > 0) {
        // Set client details from first project
        if (data[0]?.jobRole_details?.client_details) {
          setClientImage(data[0].jobRole_details.client_details.image || "");
          setClientName(
            data[0].jobRole_details.client_details.name ||
              data[0].jobRole_details.client_details.display_name ||
              "Client"
          );
        }

        const transformedProjects = data.map((project) => ({
          id: project.id,
          name: project.name,
          image: project.jobRole_details?.client_details?.image || "",
          phase: project.project_phase
            ? project.project_phase.charAt(0).toUpperCase() +
              project.project_phase.slice(1)
            : "In Progress",
          status: project.project_status || "started",
        }));

        console.log("Transformed projects:", transformedProjects);
        setProjects(transformedProjects);
      } else {
        console.log("No projects data received or empty array");
        setProjects([]);
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter projects based on status and search query
  const filteredProjects = projects.filter((project) => {
    if (
      filterStatus !== "All Status" &&
      project.phase.toLowerCase() !== filterStatus.toLowerCase()
    ) {
      return false;
    }
    if (
      searchQuery &&
      !project.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const handleDelete = (projectId) => {
    setShowDeleteModal(true);

    setCurrentRole({ id: projectId });
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCurrentRole(null);
  };

  const handleDeleteConfirm = async (projectId) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await fetch(
        `${BASE_URL}/project/archive_project?project_id=${projectId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete project");
      }

      setShowDeleteModal(false);
      setCurrentRole(null);

      // Remove the deleted project from the list
      setProjects(projects.filter((project) => project.id !== projectId));
    } catch (err) {
      console.error("Error deleting project:", err);
    }
  };

  return (
    <>
      <div className="role-container">
        <div className="role-container-inner">
          {/* Header Section */}
          <div className="role-header">
            <div className="role-back-and-title">
              <button className="role-back-button" onClick={handleBack}>
                <span className="role-back-icon">
                  <ChevronLeft />
                </span>
              </button>
              <div className="role-company-info">
                <div className="role-logo">
                  {loading ? (
                    <div className="skeleton skeleton-logo"></div>
                  ) : clientImage ? (
                    <img
                      src={clientImage}
                      alt={clientName}
                      className="role-client-logo"
                    />
                  ) : (
                    <div className="role-client-logo-placeholder">
                      {clientName.charAt(0)}
                    </div>
                  )}
                </div>
                <h1 className="role-company-title">
                  {loading ? (
                    <div className="skeleton skeleton-client-name"></div>
                  ) : (
                    clientName || "Client"
                  )}
                </h1>
              </div>
            </div>
          </div>

          {/* Section Title */}
          <h2 className="role-section-title">
            {loading ? (
              <div className="skeleton skeleton-section-title"></div>
            ) : (
              "Projects"
            )}
          </h2>

          {/* Filters Section */}
          <div className="role-filters-container">
            <div className="role-filter-section">
              <span className="role-filter-icon">
                <Funnel size="16px" />
              </span>
              <span className="role-filter-label">Filters</span>
              <div className="role-dropdown">
                {loading ? (
                  <div className="skeleton skeleton-dropdown"></div>
                ) : (
                  <select
                    className="role-status-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All Status">All Status</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                )}
              </div>
            </div>
            <div className="role-search-section">
              {loading ? (
                <div className="skeleton skeleton-search"></div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search Project...."
                    className="role-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button className="role-search-button">
                    <span className="role-search-icon">
                      <Search size="16px" />
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Cards Grid */}
          {loading ? (
            <div className="role-grid">
              {[...Array(4)].map((_, idx) => (
                <div className="role-card skeleton-card" key={idx}>
                  <div className="role-card-header">
                    <div className="skeleton skeleton-title"></div>
                    <div className="skeleton skeleton-logo"></div>
                  </div>
                  <div className="role-card-status">
                    <div className="skeleton skeleton-phase"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="role-error">Error: {error}</div>
          ) : filteredProjects.length === 0 ? (
            <div className="role-no-data">
              <p>No projects found.</p>
            </div>
          ) : (
            <div className="role-grid">
              {filteredProjects.map((project) => (
                <div key={project.id} className="role-card">
                  <div className="role-card-header">
                    <div className="role-name">{project.name}</div>
                    <div className="role-header-actions">
                      <div className="role-icon">
                        {project.image ? (
                          <img
                            src={project.image}
                            alt={project.name || "Project"}
                            className="role-image"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "block";
                            }}
                          />
                        ) : null}
                        <div
                          className="role-default-icon"
                          style={{ display: project.image ? "none" : "block" }}
                        >
                          🔥
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="role-card-status">
                    <span className="role-phase-label">Phase: </span>
                    <span className="role-phase-value">{project.phase}</span>
                    <span
                      className={`role-status-indicator ${
                        project.phase === "In Progress"
                          ? "progress"
                          : project.phase === "Completed"
                          ? "completed"
                          : "completed"
                      }`}
                    ></span>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(project.id)}
                      aria-label="Delete project"
                    >
                      <Trash2 color="#c12328" size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showDeleteModal && (
            <div className="role-delete-modal-overlay">
              <div className="role-delete-modal-container">
                <div className="role-delete-modal-content">
                  <h3>Confirm Delete Project?</h3>
                  <div className="role-delete-modal-buttons">
                    <button
                      className="role-delete-modal-button role-delete-cancel"
                      onClick={cancelDelete}
                    >
                      No
                    </button>
                    <button
                      className="role-delete-modal-button role-delete-confirm"
                      onClick={() => handleDeleteConfirm(currentRole.id)}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ViewProjects;
